import { useLanguage, curriculumData, CONTACT } from "@/i18n";
import { Reveal } from "@/components/Reveal";
import { ChevronDown } from "lucide-react";

function Timeline({ title, items }) {
  return (
    <Reveal>
      <h2 className="font-serif text-2xl sm:text-3xl font-light text-slate-900 tracking-tight">{title}</h2>
      <ol className="mt-8 space-y-0 border-l border-slate-200">
        {items.map((item, i) => (
          <li key={i} className="relative pl-8 pb-10 last:pb-0" data-testid={`timeline-item-${i}`}>
            <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-brand" />
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">{item.year}</p>
            <p className="mt-1.5 font-serif text-xl sm:text-2xl text-slate-900">{item.title}</p>
            <p className="mt-1 text-sm font-light text-slate-500">{item.place}</p>
          </li>
        ))}
      </ol>
    </Reveal>
  );
}

export default function Curriculum() {
  const { lang, t } = useLanguage();
  const data = curriculumData[lang];

  return (
    <div data-testid="curriculum-page" className="pt-24 sm:pt-32">
      <section className="container-editorial pb-16">
        <Reveal>
          <p className="eyebrow">{t.curriculum.eyebrow}</p>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.1] text-slate-900 tracking-tight">
            {t.curriculum.title}
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg font-light leading-relaxed text-slate-600" data-testid="curriculum-profile">
            {data.profile}
          </p>
        </Reveal>
      </section>

      <section className="container-editorial pb-20 sm:pb-28 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-7 space-y-16">
          <Timeline title={t.curriculum.formation} items={data.formation} />
          <Timeline title={t.curriculum.experiences} items={data.experiences} />
          <Reveal delay={0.05}>
            <details
              className="group border border-slate-200 bg-white"
              data-testid="curriculum-seminars"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-8 [&::-webkit-details-marker]:hidden">
                <h2 className="font-serif text-2xl sm:text-3xl font-light text-slate-900 tracking-tight">
                  {data.seminarsTitle}
                </h2>
                <ChevronDown className="h-5 w-5 shrink-0 text-brand transition-transform duration-300 group-open:rotate-180" />
              </summary>
              <div className="border-t border-slate-100 px-8 pb-8 pt-6">
                <p className="eyebrow">{data.seminarsEyebrow}</p>
                <ul className="mt-5 space-y-4">
                  {data.seminars.map((s, i) => (
                    <li key={i} className="text-sm font-light text-slate-600" data-testid={`seminar-item-${i}`}>
                      <span className="text-slate-900">{s.title}</span>
                      <span className="mt-0.5 block text-[11px] uppercase tracking-[0.2em] text-slate-400">
                        {s.meta}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="eyebrow mt-8">{data.emergencyTitle}</p>
                <ul className="mt-5 space-y-4">
                  {data.emergency.map((s, i) => (
                    <li key={i} className="text-sm font-light text-slate-600" data-testid={`emergency-item-${i}`}>
                      <span className="text-slate-900">{s.title}</span>
                      <span className="mt-0.5 block text-[11px] uppercase tracking-[0.2em] text-slate-400">
                        {s.meta}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </details>
          </Reveal>
        </div>

        <aside className="md:col-span-5 space-y-10">
          <Reveal delay={0.1}>
            <div className="bg-brand-linen border border-slate-200 p-8">
              <h3 className="font-serif text-xl text-slate-900">{t.curriculum.specializations}</h3>
              <ul className="mt-5 space-y-3">
                {data.specializations.map((s) => (
                  <li key={s} className="flex items-baseline gap-3 text-sm font-light text-slate-700">
                    <span className="h-1 w-1 rounded-full bg-brand shrink-0" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="border border-slate-200 p-8">
              <h3 className="eyebrow">{t.curriculum.philosophy}</h3>
              <p className="mt-4 font-serif italic text-xl leading-relaxed text-brand">
                "{t.about.quote}"
              </p>
              <p className="mt-4 text-sm font-light leading-relaxed text-slate-600">
                {t.curriculum.philosophyBody}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="border border-slate-200 p-8">
              <p className="eyebrow">{t.nav.contatti}</p>
              <p className="mt-4 text-sm font-light leading-relaxed text-slate-600" data-testid="curriculum-contact-phone">
                {CONTACT.phone}
              </p>
            </div>
          </Reveal>
        </aside>
      </section>
    </div>
  );
}
